import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaSalvageTitlePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Salvage Title</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Salvage Title Information</h1>
        <p className="text-obsidian-300">Understanding salvage titles in Nevada — when they&apos;re required, how to apply, and what they mean for your vehicle.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What Is a Salvage Title?</h2>
          <p>A salvage title is issued when a vehicle has been declared a total loss by an insurance company — typically when repair costs exceed a certain percentage of the vehicle&apos;s value. In Nevada, a salvage title must be applied for within <strong>30 days</strong> of the vehicle being declared salvage.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">When Is a Salvage Title Required?</h2>
          <ul className="space-y-3">
            {[
              'Vehicle is declared a total loss by an insurance company',
              'Vehicle is damaged to the point where repair costs exceed its market value',
              'Vehicle is stolen and not recovered within a certain period',
              'Vehicle is flood-damaged beyond a certain threshold',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-error-warning-line text-red-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Salvage Title Process</h2>
          <ol className="space-y-4">
            {[
              { step: '1', title: 'Apply Within 30 Days', desc: 'The salvage title application (Form VP 175) must be submitted within 30 days of the vehicle being declared salvage.' },
              { step: '2', title: 'Lienholder Involvement', desc: 'If there is a lien on the vehicle, the lienholder must apply for the salvage title.' },
              { step: '3', title: 'Surrender Original Title', desc: 'The original title must be surrendered when applying for a salvage title.' },
              { step: '4', title: 'Rebuilt Salvage', desc: 'If the vehicle is repaired and you want to drive it again, you must apply for a "Rebuilt Salvage" title after passing a DMV inspection.' },
            ].map(item => (
              <li key={item.step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-obsidian-900 text-gold-400 rounded-full font-bold text-sm flex-shrink-0">{item.step}</div>
                <div><p className="font-semibold text-obsidian-900 text-sm">{item.title}</p><p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p></div>
              </li>
            ))}
          </ol>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Impact of a Salvage Title</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <ul className="space-y-2 text-sm text-amber-800">
              {[
                'Vehicles with salvage titles cannot be legally driven on public roads',
                'Insurance may be difficult or expensive to obtain',
                'Resale value is significantly reduced',
                'Some lenders will not finance vehicles with salvage titles',
                'Must pass DMV inspection to obtain a "Rebuilt Salvage" title before driving',
              ].map(item => (
                <li key={item} className="flex items-start space-x-2"><i className="ri-information-line flex-shrink-0 mt-0.5"></i><span>{item}</span></li>
              ))}
            </ul>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Questions About Salvage Titles?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can help you navigate the salvage title process in Nevada.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaSalvageTitlePage;
